/*
 * at-trade-quote.js - the contact-first quote form.
 *
 * Markup: snippets/at-trade-quote-dialog.liquid (a <template> plus a JSON config).
 * Styles: assets/at-trade-quote.css. Launchers: sections/at-trade-in-form.liquid.
 *
 * Flow: step 1 name and email -> step 2 one item at a time -> step 3 review and send ->
 * sent, with an optional note. Step 1 Continue records a capture (fire-and-forget) so an
 * abandoned form can be chased. The final POST is the same multipart request the old form
 * sent, plus contact_name and capture_id.
 *
 * Carried over from the old form (sections/at-trade-in-form-legacy.liquid): compressImage,
 * HEIC handling, tracking capture, the idempotent retry, the ATEmailCheck gate, and the
 * GA4 event names. Every event is listed in shopify-backup/ANALYTICS_EVENTS.md.
 *
 * No user-facing copy lives in this file. It all comes from the theme settings via the
 * JSON config. The condition values are data, not copy: the API stores them as written.
 */
(function () {
  'use strict';
  // A second launcher on the page loads this file again. Only the first copy runs.
  if (window.__atTradeQuoteLoaded) return;
  window.__atTradeQuoteLoaded = true;

  // Resume key from a chase email link (#resume=<key>). It is a bearer credential. The first
  // script in layout/theme.liquid takes it out of the address bar before GTM and the pixels
  // load, and hands the raw value over as window.__atTqResume, which is deleted here at once.
  // If that script did not run, the hash is stripped here instead, before anything else in
  // this file runs. The key is kept only in this closure until the resume request is sent.
  // It is never logged and never sent to GA.
  var RESUME_TIMEOUT = 5000;
  var resumeKey = takeResumeKey();

  function takeResumeKey() {
    var raw = null;
    try {
      if (typeof window.__atTqResume === 'string') raw = window.__atTqResume;
      delete window.__atTqResume;
    } catch (e) { /* not deletable: still use the value */ }
    if (raw === null) {
      var hash = location.hash || '';
      if (hash.indexOf('#resume=') !== 0) return null;
      try { history.replaceState(null, '', location.pathname + location.search); } catch (e) { /* key stays; still use it */ }
      raw = hash.slice('#resume='.length);
    }
    var key;
    try { key = decodeURIComponent(raw); } catch (e) { return null; }
    return /^[A-Za-z0-9_-]{1,64}$/.test(key) ? key : null;
  }

  var CONDITIONS = ['Like New', 'Excellent', 'Very Good', 'Good', 'Fair', 'Spares or Repair'];
  var MAX_ITEMS = 10;
  var MAX_PHOTOS_PER_ITEM = 20;
  var MAX_PHOTOS_TOTAL = 50;   // the API limit per submission
  var MAX_QTY = 20;
  var MAX_NOTE = 2000;
  var MAX_FILE = 10 * 1024 * 1024;
  var FORM_TYPE = 'main';      // keeps the GA4 funnel continuous with the old form
  // Idempotent auto-retry for dropped mobile submits (spec: trade-in-mobile-submit-reliability).
  var RETRY = true;
  // Extensions the public API accepts (magic-byte checked server-side). Anything else
  // (webp, gif, bmp...) is re-encoded to JPEG regardless of size.
  var API_IMAGE_EXTS = ['.jpg', '.jpeg', '.png', '.heic', '.heif', '.avif'];
  var TRACKING_FIELDS = ['ga4_client_id', 'gclid', 'fbclid', 'msclkid', 'li_fat_id',
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  var ICON = {
    pencil: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/><path d="m15 5 4 4"/></svg>',
    trash: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>',
    x: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M20 6 9 17l-5-5"/></svg>',
    image: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>'
  };

  // ---------- small helpers ----------

  function track(name, params) {
    if (typeof window.gtag === 'function') window.gtag('event', name, params);
  }

  // For events that can fire while the page loads (an auto-open, a launcher already in view):
  // GTM loads async, so retry for up to 3 seconds, as the old form did for its page events.
  function trackSoon(name, params, n) {
    if (typeof window.gtag === 'function') return window.gtag('event', name, params);
    if ((n || 0) < 15) setTimeout(function () { trackSoon(name, params, (n || 0) + 1); }, 200);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  // "{count} photos" style substitution. Unknown tokens are left as written.
  function fill(tpl, vars) {
    return String(tpl == null ? '' : tpl).replace(/\{(\w+)\}/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(vars || {}, k) ? String(vars[k]) : m;
    });
  }

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    // Fallback (older browsers): RFC 4122 v4 from getRandomValues.
    var b = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
    var h = Array.prototype.map.call(b, function (x) { return (x < 16 ? '0' : '') + x.toString(16); });
    return h.slice(0, 4).join('') + '-' + h.slice(4, 6).join('') + '-' + h.slice(6, 8).join('') + '-' +
      h.slice(8, 10).join('') + '-' + h.slice(10).join('');
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function session(get, key, value) {
    try {
      if (get) return window.sessionStorage.getItem(key);
      window.sessionStorage.setItem(key, value);
    } catch (e) { /* private mode: tracking just degrades */ }
    return null;
  }

  function isHeic(file) { return /\.(heic|heif)$/i.test(file.name || ''); }
  function extOf(file) { return ((file.name || '').split('.').pop() || '').toLowerCase(); }

  function isImage(file) {
    return (file.type || '').indexOf('image/') === 0 ||
      ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif', 'avif', 'bmp', 'tiff'].indexOf(extOf(file)) !== -1 ||
      file.type === ''; // Android content-provider photos can arrive with no MIME type and no extension
  }

  // The API checks a photo's leading bytes against its extension and rejects the whole
  // request on a mismatch ("File content does not match extension"). So name each photo
  // after what it really is. Keep the brands in step with Prism's file_validation.py.
  // The same block lives in at-trade-quote.js, at-bulk-sell-form.liquid and the
  // more-info page (audiotrader-collection, collection-form/index.js).
  var SNIFF_FTYP = { heic: 'heic', heix: 'heic', mif1: 'mif1', hevc: 'heic', avif: 'avif', avis: 'avif' };
  function sniffImageExt(b) {
    if (b[0] === 0xFF && b[1] === 0xD8 && b[2] === 0xFF) return 'jpg';
    if (b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4E && b[3] === 0x47) return 'png';
    var s = String.fromCharCode.apply(null, b);
    return s.slice(4, 8) === 'ftyp' ? (SNIFF_FTYP[s.slice(8, 12)] || '') : '';
  }
  function matchNameToContent(file) {
    var head = file.slice && typeof File !== 'undefined' ? file.slice(0, 12) : null;
    if (!head || !head.arrayBuffer) return Promise.resolve(file);
    return head.arrayBuffer().then(function (buf) {
      var real = sniffImageExt(new Uint8Array(buf));
      var dot = file.name.lastIndexOf('.');
      var ext = dot === -1 ? '' : file.name.slice(dot + 1).toLowerCase();
      var base = dot === -1 ? file.name : file.name.slice(0, dot);
      var claimed = { jpeg: 'jpg', heif: 'heic' }[ext] || ext;
      // mif1 is a generic container brand that HEIC and AVIF both use: between those two,
      // trust the name. The API only takes mif1 as HEIC, so any other name becomes .heic.
      if (real === 'mif1') real = claimed === 'avif' ? 'avif' : 'heic';
      if (real === claimed) return file;
      if (real) return new File([file], base + '.' + real, { type: real === 'jpg' ? 'image/jpeg' : 'image/' + real, lastModified: file.lastModified });
      // Not a type the API takes. If the name claims one, drop the extension: compressImage
      // then has to re-encode it to JPEG, or refuse it before upload.
      if (['jpg', 'png', 'heic', 'avif'].indexOf(claimed) === -1) return file;
      return new File([file], base, { type: '', lastModified: file.lastModified });
    }, function () { return file; });
  }

  // Carried over verbatim in behaviour from the old form.
  function compressImage(file, maxDim, quality) {
    maxDim = maxDim || 1600; quality = quality || 0.85;
    var TARGET = MAX_FILE;
    return new Promise(function (resolve) {
      if (file.type && file.type.indexOf('image/') !== 0) return resolve(file);
      var needsJpegName = !API_IMAGE_EXTS.some(function (ext) { return file.name.toLowerCase().endsWith(ext); });
      var extAllowed = !needsJpegName;
      if (extAllowed && file.size < 100 * 1024) return resolve(file);
      var img = new Image();
      var url = URL.createObjectURL(file);
      img.onload = function () {
        URL.revokeObjectURL(url);
        if (extAllowed && img.width <= maxDim && img.height <= maxDim && file.size <= TARGET) return resolve(file);
        var attempts = [{ dim: maxDim, q: quality }, { dim: 1280, q: 0.8 }, { dim: 1024, q: 0.75 }];
        var encode = function (idx) {
          var a = attempts[idx];
          var scale = Math.min(1, a.dim / img.width, a.dim / img.height);
          var w = Math.round(img.width * scale), h = Math.round(img.height * scale);
          var canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          canvas.toBlob(function (blob) {
            if (!blob) return resolve(needsJpegName ? null : file);
            if (blob.size > TARGET && idx < attempts.length - 1) return encode(idx + 1);
            if (blob.size >= file.size && !needsJpegName) return resolve(file);
            var jpgName = /\.[^.]+$/.test(file.name) ? file.name.replace(/\.[^.]+$/, '.jpg') : file.name + '.jpg';
            resolve(new File([blob], jpgName, { type: 'image/jpeg', lastModified: file.lastModified }));
          }, 'image/jpeg', a.q);
        };
        encode(0);
      };
      img.onerror = function () { URL.revokeObjectURL(url); resolve(null); };
      img.src = url;
    });
  }

  // ---------- the form ----------

  function TradeQuote(cfg, root) {
    this.cfg = cfg;
    this.t = cfg.strings || {};
    this.root = root;
    this.$ = function (sel) { return root.querySelector(sel); };
    this.$$ = function (sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };
    this.urls = new Map();     // File -> object URL, so thumbnails are not re-created on every render
    this.noThumb = new WeakSet(); // Files this browser cannot draw (HEIC outside Safari)
    this.tracking = {};
    this.isOpen = false;
    this.flags = {};
    this.reset(true);
  }

  var P = TradeQuote.prototype;

  // The contact the form starts from: a resumed capture (the person the chase email went to)
  // beats the signed-in Shopify customer.
  P.prefill = function () {
    var c = this.resumed || this.cfg.customer || {};
    return { name: c.name || '', email: c.email || '' };
  };

  P.reset = function (keepContact) {
    if (!keepContact || !this.contact) this.contact = this.prefill();
    // A resumed capture keeps its id until a submission converts it (showDone clears it).
    this.captureId = (this.resumed && this.resumed.captureId) || uuid();
    this.captured = null;
    this.items = [];
    this.newDraft();
    this.step = 1;
    this.done = false;
    this.submitting = false;
    this.idemKey = null;
    this.result = null;
    this.touched = {};
  };

  P.newDraft = function () {
    this.draft = { makeModel: '', condition: '', quantity: 1, photos: [] };
    this.editIndex = null;
    this.draftTouched = false;
    this.draftShowErrors = false;
  };

  // ----- derived state -----

  P.nameOk = function () { var n = this.contact.name.trim(); return n.length >= 1 && n.length <= 120; };
  P.emailOk = function () {
    var v = this.contact.email.trim();
    if (!v) return false;
    if (window.ATEmailCheck) return !window.ATEmailCheck.inspect(v).malformed;
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v) && v.length <= 254;
  };
  P.contactOk = function () { return this.nameOk() && this.emailOk(); };
  P.draftErrors = function () {
    var d = this.draft, e = [];
    if (!d.makeModel.trim()) e.push('mm');
    if (!d.condition) e.push('cond');
    if (!d.photos.length) e.push('photos');
    return e;
  };
  P.canGoTo = function (n) {
    if (this.done || this.submitting) return false;
    if (n === 1) return true;
    if (n === 2) return this.contactOk();
    if (n === 3) return this.contactOk() && this.items.length > 0;
    return false;
  };
  P.totalPhotos = function () {
    var self = this, n = this.draft.photos.length;
    this.items.forEach(function (it, i) { if (i !== self.editIndex) n += it.photos.length; });
    return n;
  };
  P.isDirty = function () {
    if (this.done) return false;
    var c = this.prefill();
    return this.items.length > 0 || this.draftTouched ||
      this.contact.name.trim() !== c.name.trim() || this.contact.email.trim() !== c.email.trim();
  };
  P.plural = function (n, one, other) { return fill(n === 1 ? this.t[one] : this.t[other], { count: n }); };

  // ----- mounting -----

  P.mount = function () {
    var self = this, root = this.root;
    document.body.appendChild(root);

    root.addEventListener('click', function (e) { self.onClick(e); });
    root.addEventListener('keydown', function (e) { self.onKeydown(e); });
    window.addEventListener('popstate', function () { self.onPopState(); });

    var name = this.$('#at-tq-name'), email = this.$('#at-tq-email');
    // Any edit, contact details included, must give the next send a fresh Idempotency-Key,
    // or the API would replay an earlier attempt's result (the old form did the same).
    name.addEventListener('input', function () { self.contact.name = name.value; self.idemKey = null; self.touched.name = true; self.validateContact(false); self.render(); });
    email.addEventListener('input', function () { self.contact.email = email.value; self.idemKey = null; self.touched.email = true; self.validateContact(false); self.render(); });
    // Shared typo catcher: "did you mean gmail.com?" on blur, cleared as they type. Attached
    // before our own blur handler so ours runs second: a malformed address gets one message,
    // ours, so its "Please enter a valid email address" is taken down again.
    if (window.ATEmailCheck) window.ATEmailCheck.attach(email, { formType: FORM_TYPE });
    name.addEventListener('blur', function () { if (self.touched.name) self.showContactError('name', !self.nameOk()); });
    email.addEventListener('blur', function () {
      var msg = email._atMsg;
      if (msg && msg.className.indexOf('at-email-check--error') !== -1) { msg.hidden = true; msg.textContent = ''; email.style.borderColor = ''; }
      if (self.touched.email) self.showContactError('email', !self.emailOk());
    });
    [name, email].forEach(function (el) {
      el.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); self.continueFromContact(); } });
    });

    var mm = this.$('#at-tq-mm');
    mm.addEventListener('input', function () {
      self.draft.makeModel = mm.value; self.draftTouched = true;
      if (!self.flags.makeModel && mm.value.trim()) { self.flags.makeModel = true; track('trade_in_makemodel_entered', { form_type: FORM_TYPE }); }
      self.render();
    });
    mm.addEventListener('focus', function () {
      if (self.flags.step1Start) return;
      self.flags.step1Start = true;
      track('trade_in_step_1_start', { form_type: FORM_TYPE });
      if (/^\/pages\/sell-/.test(location.pathname)) track('sell_landing_cta_click', { page_path: location.pathname });
    });
    mm.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); self.saveItem(); } });

    var qty = this.$('#at-tq-qty');
    qty.addEventListener('change', function () { self.setQty(qty.value); });

    this.$$('input[name="at-tq-condition"]').forEach(function (r) {
      r.addEventListener('change', function () {
        self.draft.condition = r.value; self.draftTouched = true;
        track('trade_in_condition_selected', { condition: r.value, item_index: self.itemIndex(), form_type: FORM_TYPE });
        self.render();
      });
    });

    var file = this.$('[data-tq-file]');
    file.addEventListener('change', function () { self.addFiles(file.files); file.value = ''; });
    // Every photo is tried as a picture first. HEIC draws in Safari but not in Chrome or
    // Firefox, so only a photo the browser fails to draw gets the placeholder tile.
    // 'error' does not bubble, hence the capture listener.
    this.root.addEventListener('error', function (e) {
      var img = e.target, found = false;
      if (!img || img.tagName !== 'IMG') return;
      self.urls.forEach(function (u, f) { if (u === img.src && !self.noThumb.has(f)) { self.noThumb.add(f); found = true; } });
      if (found) self.render();
    }, true);
    var drop = this.$('[data-tq-drop]');
    drop.addEventListener('dragover', function (e) { e.preventDefault(); drop.classList.add('is-dragover'); });
    drop.addEventListener('dragleave', function () { drop.classList.remove('is-dragover'); });
    drop.addEventListener('drop', function (e) {
      e.preventDefault(); drop.classList.remove('is-dragover');
      if (e.dataTransfer && e.dataTransfer.files) self.addFiles(e.dataTransfer.files);
    });

    var note = this.$('#at-tq-note');
    note.addEventListener('input', function () {
      self.$('[data-tq-action="note"]').disabled = !note.value.trim() || self.noteBusy;
      self.fieldError('note', null);
    });

    // Blocked primary presses land on the wrapper (the disabled button has pointer-events: none).
    this.$('[data-tq-primary-wrap]').addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('button');
      if (btn && !btn.disabled) return;
      self.noteBlocked();
    });

    // The condition guide link keeps its href as the no-JavaScript fallback (a new tab). With
    // JavaScript it opens the guide pop-up instead, so the new-tab wording is removed.
    var guideLink = this.$('[data-tq-guide-link]');
    if (guideLink) {
      guideLink.removeAttribute('target');
      guideLink.setAttribute('aria-haspopup', 'dialog');
      var newTab = guideLink.querySelector('.sr-only');
      if (newTab) newTab.remove();
    }

    if (window.ATFormEngagement) window.ATFormEngagement.track(root, FORM_TYPE);
    this.captureTracking();
    this.syncInputs();
    this.render();
  };

  // ----- events -----

  P.onClick = function (e) {
    var el = e.target.closest ? e.target.closest('[data-tq-action],[data-tq-goto],[data-tq-qty],[data-tq-primary],[data-tq-edit],[data-tq-delete],[data-tq-remove-photo],[data-tq-bulk],[data-tq-guide-close]') : null;
    if (!el || !this.root.contains(el) || el.disabled) return;
    if (el.hasAttribute('data-tq-guide-close')) return this.closeGuide(false);
    if (el.getAttribute('data-tq-action') === 'guide') {
      if (e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // let a new-tab click through
      e.preventDefault();
      return this.openGuide(el);
    }
    if (el.hasAttribute('data-tq-bulk')) { track('trade_in_bulk_link_click', { item_count: this.items.length, form_type: FORM_TYPE }); return; }
    if (el.hasAttribute('data-tq-goto')) return this.goTo(parseInt(el.getAttribute('data-tq-goto'), 10));
    if (el.hasAttribute('data-tq-qty')) return this.setQty(this.draft.quantity + parseInt(el.getAttribute('data-tq-qty'), 10));
    if (el.hasAttribute('data-tq-edit')) return this.editItem(parseInt(el.getAttribute('data-tq-edit'), 10));
    if (el.hasAttribute('data-tq-delete')) return this.deleteItem(parseInt(el.getAttribute('data-tq-delete'), 10));
    if (el.hasAttribute('data-tq-remove-photo')) return this.removePhoto(parseInt(el.getAttribute('data-tq-remove-photo'), 10));
    if (el.hasAttribute('data-tq-primary')) {
      var s = el.getAttribute('data-tq-primary');
      if (s === '1') return this.continueFromContact();
      if (s === '2') return this.saveItem();
      if (s === '3') return this.submit();
    }
    switch (el.getAttribute('data-tq-action')) {
      case 'close': return this.requestClose();
      case 'back': return this.goTo(this.step - 1);
      case 'cancel': return this.cancelItem();
      case 'add': return this.addAnother();
      case 'pick': return this.pickPhotos();
      case 'another': return this.submitAnother();
      case 'note': return this.sendNote();
      case 'note-again': return this.noteAgain();
    }
  };

  P.onKeydown = function (e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (this.guideOpen) return this.closeGuide(false);
      if (this.confirmState) return this.resolveConfirm(false);
      if (this.busy) return;
      return this.requestClose();
    }
    if (e.key === 'Tab') this.trapFocus(e);
  };

  P.trapFocus = function (e) {
    var layer = this.guideOpen ? this.$('[data-tq-guide] .at-tq-guide__panel')
      : this.confirmState ? this.$('[data-tq-confirm] .at-tq__card') : this.root;
    if (this.busy && !this.confirmState && !this.guideOpen) { e.preventDefault(); return; }
    var nodes = Array.prototype.filter.call(
      layer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]):not([tabindex="-1"]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'),
      function (n) { return n.getClientRects().length > 0 && !n.closest('[hidden]'); });
    if (!this.confirmState && !this.guideOpen) nodes = nodes.filter(function (n) { return !n.closest('[data-tq-confirm],[data-tq-busy],[data-tq-guide]'); });
    // The list includes every condition radio even though the browser gives the group a single
    // tab stop. That is harmless: only the first and last nodes are used, and neither is a radio.
    if (!nodes.length) { e.preventDefault(); return; }
    var first = nodes[0], last = nodes[nodes.length - 1];
    var active = document.activeElement;
    if (e.shiftKey && (active === first || !layer.contains(active))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (active === last || !layer.contains(active))) { e.preventDefault(); first.focus(); }
  };

  // ----- open and close -----

  // If opening fails part way, undo it: a half-open form leaves the page inert and
  // scroll-locked with the dialog still hidden, and isOpen stuck true so every later
  // click is ignored. That is what a visitor reads as the site freezing.
  P.open = function (trigger) {
    if (this.isOpen) return;
    try { this.openNow(trigger); } catch (e) { this.abandonOpen(); throw e; }
  };

  P.abandonOpen = function () {
    (this.inerted || []).forEach(function (el) { el.inert = false; el.removeAttribute('aria-hidden'); });
    this.inerted = [];
    document.documentElement.classList.remove('at-tq-locked');
    this.root.classList.remove('is-entering');
    this.root.hidden = true;
    this.isOpen = false;
    if (this.pushed) { this.pushed = false; this.ignorePops = (this.ignorePops || 0) + 1; history.back(); }
  };

  P.openNow = function (trigger) {
    var self = this, root = this.root;
    this.isOpen = true;
    this.returnFocus = document.activeElement;
    if (this.done) { this.revokeAll(); this.reset(true); this.syncInputs(); }
    // Everything else on the page becomes inert while the dialog is open,
    // except Shopify's cookie consent UI: it paints above the dialog, so an
    // inert banner would cover the footer buttons and could not be dismissed.
    this.inerted = [];
    Array.prototype.forEach.call(document.body.children, function (el) {
      if (el === root || el.inert || el.tagName === 'SCRIPT' || el.tagName === 'TEMPLATE') return;
      // getAttribute, not el.id: a <form> holding a field named "id" returns that field
      // from .id, and .indexOf on it threw, which froze the page (2026-09-29).
      if ((el.getAttribute('id') || '').indexOf('shopify-pc__') === 0) return;
      el.inert = true; el.setAttribute('aria-hidden', 'true'); self.inerted.push(el);
    });
    document.documentElement.classList.add('at-tq-locked');
    root.classList.remove('is-leaving');
    root.hidden = false;
    if (!reducedMotion()) {
      root.classList.add('is-entering');
      this.afterAnimation(function () { root.classList.remove('is-entering'); });
    }
    this.pushMarker();
    this.render();
    this.focusStep(true);
    this.trackFormView();
    trackSoon('trade_in_form_open', { page_path: location.pathname, form_type: FORM_TYPE, trigger: trigger || 'click' });
    this.trackStep();
  };

  // trade_in_form_view keeps the old form's meaning: once per page load, when the form is
  // first seen. That is the launcher panel at least half in view, or the dialog opening,
  // whichever comes first. Same params as the old event, so the series stays comparable.
  P.trackFormView = function () {
    if (this.viewTracked) return;
    this.viewTracked = true;
    if (this.viewObserver) { this.viewObserver.disconnect(); this.viewObserver = null; }
    trackSoon('trade_in_form_view', { page_path: location.pathname, referrer: document.referrer || '(direct)', form_type: FORM_TYPE });
  };

  P.watchLaunchers = function () {
    var self = this, panels = document.querySelectorAll('[data-at-tq-launcher]');
    if (!panels.length || typeof IntersectionObserver !== 'function') return;
    this.viewObserver = new IntersectionObserver(function (entries) {
      if (entries.some(function (en) { return en.isIntersecting && en.intersectionRatio >= 0.5; })) self.trackFormView();
    }, { threshold: 0.5 });
    Array.prototype.forEach.call(panels, function (el) { self.viewObserver.observe(el); });
  };

  P.afterAnimation = function (fn) {
    var root = this.root, doneOnce = false;
    var finish = function () { if (doneOnce) return; doneOnce = true; root.removeEventListener('animationend', onEnd); fn(); };
    var onEnd = function (e) { if (e.target === root) finish(); };
    root.addEventListener('animationend', onEnd);
    setTimeout(finish, 450); // safety net if animationend never fires
  };

  // ----- condition guide pop-up -----
  // Opens over the form from the "See our condition guide" link, so nobody leaves the form to
  // read it. The form underneath is not touched. It adds its own history entry on top of the
  // dialog's, so Back closes the guide and a second Back reaches the dialog as before.

  P.onAnimEnd = function (el, fn) {
    var doneOnce = false;
    var finish = function () { if (doneOnce) return; doneOnce = true; el.removeEventListener('animationend', onEnd); fn(); };
    var onEnd = function (e) { if (e.target === el) finish(); };
    el.addEventListener('animationend', onEnd);
    setTimeout(finish, 450); // safety net if animationend never fires
  };

  P.openGuide = function (link) {
    if (this.guideOpen || !this.isOpen) return;
    var guide = this.$('[data-tq-guide]'), panel = guide.querySelector('.at-tq-guide__panel');
    this.guideOpen = true;
    this.guideReturn = link || document.activeElement;
    guide.classList.remove('is-leaving');
    guide.hidden = false;
    this.$('[data-tq-guide-body]').scrollTop = 0;
    if (!reducedMotion()) {
      guide.classList.add('is-entering');
      this.onAnimEnd(panel, function () { guide.classList.remove('is-entering'); });
    }
    try {
      var base = history.state && typeof history.state === 'object' ? history.state : {};
      var state = {};
      Object.keys(base).forEach(function (k) { state[k] = base[k]; });
      state.atTradeQuoteGuide = true;
      history.pushState(state, '');
      this.guidePushed = true;
    } catch (e) { this.guidePushed = false; }
    this.$('#at-tq-guide-title').focus({ preventScroll: true });
    track('trade_in_condition_guide_opened', { form_type: FORM_TYPE, item_index: this.itemIndex(), step: this.step });
  };

  P.closeGuide = function (fromHistory) {
    if (!this.guideOpen) return;
    var self = this, guide = this.$('[data-tq-guide]'), panel = guide.querySelector('.at-tq-guide__panel');
    this.guideOpen = false;
    if (this.guidePushed) {
      this.guidePushed = false;
      if (!fromHistory) { this.ignorePops = (this.ignorePops || 0) + 1; history.back(); }
    }
    var finish = function () {
      if (self.guideOpen) return; // reopened during the close animation
      guide.hidden = true;
      guide.classList.remove('is-leaving');
      var back = self.guideReturn;
      if (back && self.root.contains(back) && back.getClientRects().length) back.focus({ preventScroll: true });
    };
    if (reducedMotion()) return finish();
    guide.classList.remove('is-entering');
    guide.classList.add('is-leaving');
    this.onAnimEnd(panel, finish);
  };

  // ----- browser Back -----
  // Opening adds one history entry, so the phone's Back button closes the form instead of
  // leaving the page and losing it. Closing any other way removes that entry again.

  P.pushMarker = function () {
    if (this.pushed) return;
    try {
      var base = history.state && typeof history.state === 'object' ? history.state : {};
      var state = {};
      Object.keys(base).forEach(function (k) { state[k] = base[k]; });
      state.atTradeQuote = true;
      history.pushState(state, '');
      this.pushed = true;
    } catch (e) { this.pushed = false; }
  };

  P.onPopState = function () {
    if (this.ignorePops) { this.ignorePops--; return; } // the history.back() in close() or closeGuide()
    // The guide's entry sits on top of the dialog's, so Back closes only the guide.
    if (this.guideOpen && this.guidePushed) { this.guidePushed = false; return this.closeGuide(true); }
    if (!this.isOpen || !this.pushed) return;
    this.pushed = false; // Back has already removed our entry
    if (this.submitting) { this.pushMarker(); return; } // never abandon a send in flight
    if (this.confirmState) this.resolveConfirm(false);
    if (!this.isDirty()) return this.closeClean(true);
    this.pushMarker(); // stay on the form; another Back asks again
    this.confirmLeave();
  };

  P.close = function (fromHistory) {
    if (!this.isOpen) return;
    var self = this, root = this.root;
    this.isOpen = false;
    // Take down a guide still open on top, and remove its history entry with the dialog's.
    var steps = 0;
    if (this.guideOpen) {
      this.guideOpen = false;
      this.$('[data-tq-guide]').hidden = true;
      if (this.guidePushed) { this.guidePushed = false; steps++; }
    }
    if (this.pushed) { this.pushed = false; steps++; }
    if (steps && !fromHistory) { this.ignorePops = (this.ignorePops || 0) + 1; history.go(-steps); }
    var finish = function () {
      root.hidden = true;
      root.classList.remove('is-leaving');
      (self.inerted || []).forEach(function (el) { el.inert = false; el.removeAttribute('aria-hidden'); });
      self.inerted = [];
      document.documentElement.classList.remove('at-tq-locked');
      if (self.returnFocus && document.contains(self.returnFocus) && self.returnFocus.focus) self.returnFocus.focus();
    };
    if (reducedMotion()) return finish();
    root.classList.remove('is-entering');
    root.classList.add('is-leaving');
    this.afterAnimation(finish);
  };

  P.requestClose = function () {
    if (this.submitting) return;
    if (!this.isDirty()) return this.closeClean(false);
    this.confirmLeave();
  };

  P.closeClean = function (fromHistory) {
    if (this.done) { this.revokeAll(); this.reset(true); this.syncInputs(); } // reset drops the items, so free their thumbnails first
    this.close(fromHistory);
  };

  P.confirmLeave = function () {
    var self = this;
    this.confirm({ title: this.t.leave_title, body: this.t.leave_body, yes: this.t.leave_confirm, no: this.t.keep_editing })
      .then(function (ok) {
        if (!ok) return;
        self.revokeAll();
        self.reset(false);
        self.syncInputs();
        self.close(false);
      });
  };

  // ----- confirm dialog (Promise<boolean>) -----

  P.confirm = function (o) {
    var self = this, layer = this.$('[data-tq-confirm]');
    this.$('#at-tq-confirm-title').textContent = o.title || '';
    this.$('#at-tq-confirm-text').textContent = o.body || '';
    var yes = this.$('[data-tq-confirm-yes]'), no = this.$('[data-tq-confirm-no]');
    yes.textContent = o.yes || '';
    no.textContent = o.no || '';
    no.hidden = !o.no;
    yes.className = 'at-tq__btn ' + (o.tone === 'primary' ? 'at-tq__btn--primary' : 'at-tq__btn--danger');
    this.confirmReturn = document.activeElement;
    layer.hidden = false;
    return new Promise(function (resolve) {
      self.confirmState = resolve;
      yes.onclick = function () { self.resolveConfirm(true); };
      no.onclick = function () { self.resolveConfirm(false); };
      (o.no ? no : yes).focus();
    });
  };

  P.resolveConfirm = function (value) {
    var resolve = this.confirmState;
    if (!resolve) return;
    this.confirmState = null;
    this.$('[data-tq-confirm]').hidden = true;
    var back = this.confirmReturn;
    resolve(value);
    // Return focus unless the resolution moved it somewhere on purpose.
    if (back && this.root.contains(back) && back.getClientRects().length &&
        (document.activeElement === document.body || !this.root.contains(document.activeElement))) back.focus();
  };

  // ----- navigation -----

  P.goTo = function (n) {
    var self = this;
    if (n < 1 || n > 3 || n === this.step || !this.canGoTo(n)) return;
    if (this.step === 1 && n > 1) return this.continueFromContact(n);
    if (this.step === 2 && n === 3 && this.draftTouched) {
      return this.confirm({ title: this.t.discard_title, body: this.t.discard_body, yes: this.t.discard_confirm, no: this.t.keep_editing })
        .then(function (ok) { if (ok) { self.newDraft(); self.syncDraft(); self.setStep(3); } });
    }
    this.setStep(n);
  };

  P.setStep = function (n) {
    if (n === 2 && this.items.length >= MAX_ITEMS && this.editIndex === null && !this.draftTouched) n = 3;
    this.step = n;
    this.render();
    var scroll = this.$('[data-tq-scroll]');
    if (scroll) scroll.scrollTop = 0; // instant swap, no transition
    this.focusStep(false);
    this.trackStep();
  };

  P.trackStep = function () {
    var names = { 1: 'details', 2: 'item', 3: 'review' };
    var step = this.done ? 4 : this.step;
    track('trade_in_step_view', { step: step, step_name: this.done ? 'sent' : names[step], item_count: this.items.length, form_type: FORM_TYPE });
  };

  P.focusStep = function (onOpen) {
    var target;
    if (this.done) target = this.$('#at-tq-done-h');
    else if (onOpen && this.step === 1) target = !this.nameOk() ? this.$('#at-tq-name') : (!this.emailOk() ? this.$('#at-tq-email') : this.$('#at-tq-h1'));
    else if (this.step === 2 && !this.draft.makeModel) target = this.$('#at-tq-mm');
    else target = this.$('#at-tq-h' + this.step);
    if (target) target.focus({ preventScroll: true });
  };

  // ----- step 1 -----

  P.showContactError = function (field, show) {
    this.fieldError(field, show ? this.t[field + '_error'] : null);
  };

  P.validateContact = function (showAll) {
    // Live: an error already on screen clears as soon as the value becomes valid.
    var n = this.$('#at-tq-name-error'), m = this.$('#at-tq-email-error');
    if (showAll || !n.hidden) this.showContactError('name', !this.nameOk());
    if (showAll || !m.hidden) this.showContactError('email', !this.emailOk());
  };

  P.fieldError = function (field, text) {
    var box = this.$('#at-tq-' + field + '-error');
    if (!box) return;
    box.querySelector('span').textContent = text || '';
    box.hidden = !text;
    if (field === 'email') this.$('#at-tq-email-help').hidden = !!text; // the error replaces the helper line
    var input = { name: '#at-tq-name', email: '#at-tq-email', mm: '#at-tq-mm', note: '#at-tq-note', cond: '[data-tq-tiles]', photos: '[data-tq-drop]' }[field];
    var el = input && this.$(input);
    if (el) { if (text) el.setAttribute('aria-invalid', 'true'); else el.removeAttribute('aria-invalid'); }
  };

  P.continueFromContact = function (target) {
    if (!this.contactOk()) { this.noteBlocked(); return; }
    var email = this.$('#at-tq-email');
    // Authoritative email check: blocks malformed and holds once on a likely typo.
    if (window.ATEmailCheck && !window.ATEmailCheck.gate(email, { formType: FORM_TYPE })) return;
    this.contact.email = email.value.trim();
    this.contact.name = this.contact.name.trim();
    this.syncInputs();
    this.capture();
    var next = target || ((this.items.length && !this.draftTouched) ? 3 : 2);
    if (next === 3 && !this.canGoTo(3)) next = 2;
    this.setStep(next);
  };

  // Fire-and-forget. Never blocks, never surfaces an error, never retries.
  P.capture = function () {
    var url = (this.cfg.endpoints || {}).captures;
    var name = this.contact.name.trim(), email = this.contact.email.trim();
    if (!url || (this.captured && this.captured.name === name && this.captured.email === email)) return;
    var isUpdate = !!this.captured;
    this.captured = { name: name, email: email };
    var tracking = {};
    var self = this;
    Object.keys(this.tracking).forEach(function (k) { if (typeof self.tracking[k] === 'string') tracking[k] = self.tracking[k]; });
    var body = JSON.stringify({
      capture_id: this.captureId,
      contact_name: name.slice(0, 120),
      contact_email: email,
      source_page: location.pathname.slice(0, 500),
      tracking: tracking
    });
    try {
      window.fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true, credentials: 'omit', mode: 'cors' })
        .catch(function () { /* fire-and-forget */ });
    } catch (e) { /* fire-and-forget */ }
    track('trade_in_contact_captured', { form_type: FORM_TYPE, is_update: isUpdate, page_path: location.pathname });
  };

  // A resume link's contact, from the resume endpoint. It can arrive after the dialog opened,
  // so it never overwrites what the person typed. The email names the capture: once they have
  // typed an email, or step 1 was sent (the capture id is then fixed), or the form was
  // submitted, the reply is dropped. A typed name is kept and only the email is filled.
  P.applyResume = function (r) {
    if (this.done || this.captured || this.touched.email) return;
    var name = r.name.slice(0, 120), email = r.email.slice(0, 254);
    this.resumed = { name: name, email: email, captureId: r.captureId };
    this.captureId = r.captureId;
    this.contact.email = email; this.$('#at-tq-email').value = email; this.fieldError('email', null);
    if (!this.touched.name) { this.contact.name = name; this.$('#at-tq-name').value = name; this.fieldError('name', null); }
    this.idemKey = null;
    this.render();
    // Opened before the reply came: move focus as an open with these details would have done.
    var active = document.activeElement;
    if (this.isOpen && this.step === 1 && !this.touched.name &&
        (active === this.$('#at-tq-name') || active === this.$('#at-tq-email'))) this.focusStep(true);
    trackSoon('trade_in_resume_prefilled', { form_type: FORM_TYPE, form_open: this.isOpen });
  };

  // ----- step 2 -----

  P.itemIndex = function () { return this.editIndex !== null ? this.editIndex : this.items.length; };

  P.setQty = function (v) {
    var n = parseInt(v, 10);
    var q = isFinite(n) ? Math.min(Math.max(n, 1), MAX_QTY) : 1;
    var changed = q !== this.draft.quantity;
    this.draft.quantity = q;
    if (changed) { this.draftTouched = true; track('trade_in_quantity_changed', { quantity: q, item_index: this.itemIndex(), form_type: FORM_TYPE }); }
    this.syncDraft();
    this.render();
  };

  P.pickPhotos = function () {
    if (!this.flags.photoOpen) { this.flags.photoOpen = true; track('trade_in_photo_slot_opened', { slot: 'photos', form_type: FORM_TYPE }); }
    this.$('[data-tq-file]').click();
  };

  P.photoMsg = function (text) {
    var el = this.$('[data-tq-photo-msg]');
    el.textContent = text || '';
    el.hidden = !text;
  };

  P.setBusy = function (on, title, text) {
    this.busy = !!on;
    var layer = this.$('[data-tq-busy]');
    layer.hidden = !on;
    this.$('[data-tq-busy-title]').textContent = title || '';
    this.$('[data-tq-busy-title]').hidden = !title;
    this.$('[data-tq-busy-text]').textContent = text || '';
    if (!on) { this.$('[data-tq-busy-bar]').hidden = true; this.$('[data-tq-busy-hint]').hidden = true; }
  };

  // Compress, then gate on the compressed size. HEIC/HEIF is compressed too where the browser
  // can decode it (Safari): the upload is far smaller and the thumbnail is a small JPEG, not a
  // full-size original. Where it cannot, the file stays raw for server-side conversion.
  P.preparePhoto = function (file) {
    var ext = extOf(file);
    var p = matchNameToContent(file).then(function (f) {
      return compressImage(f).then(function (out) { return out || (isHeic(f) ? f : null); });
    });
    return p.then(function (out) {
      if (out === null) { track('trade_in_photo_rejected', { reason: 'unreadable', file_ext: ext, slot: 'photos', form_type: FORM_TYPE }); return null; }
      if (out.size <= MAX_FILE) return out;
      track('trade_in_photo_too_large', { file_mb: +(file.size / 1048576).toFixed(1), compressed_mb: +(out.size / 1048576).toFixed(1), file_ext: ext, slot: 'photos', form_type: FORM_TYPE });
      return null;
    });
  };

  // Photos are prepared asynchronously. Two rules stop a slow photo landing in the wrong item:
  // every batch writes to the draft that was open when it started (target), and nothing that
  // ends the draft (Save item, Send) proceeds while a batch is still in flight (this.preparing).
  P.showPreparing = function () {
    var p = this.prepProgress || { n: 1, total: 1 };
    this.prepBusy = true;
    this.setBusy(true, '', fill(this.t.photo_preparing, { n: p.n, total: p.total }));
  };

  P.addFiles = function (list) {
    var self = this, all = Array.prototype.slice.call(list || []);
    var target = this.draft;
    this.photoMsg(null);
    all.filter(function (f) { return !isImage(f); }).forEach(function (f) {
      track('trade_in_photo_rejected', { reason: 'not_image', file_ext: extOf(f), slot: 'photos', form_type: FORM_TYPE });
    });
    var imgs = all.filter(isImage);
    var rejectedNames = all.filter(function (f) { return !isImage(f); }).map(function (f) { return f.name; });
    if (!imgs.length) { if (rejectedNames.length) this.photoMsg(fill(this.t.photo_rejected, { names: rejectedNames.join(', ') })); return; }

    var roomInItem = MAX_PHOTOS_PER_ITEM - target.photos.length;
    var free = MAX_PHOTOS_TOTAL - this.totalPhotos();
    var messages = [];
    if (imgs.length > roomInItem) messages.push(fill(this.t.photo_item_cap, { max: MAX_PHOTOS_PER_ITEM }));
    var candidates = imgs.slice(0, Math.max(0, roomInItem));
    if (candidates.length > free) {
      track('trade_in_photo_cap_reached', { photo_count: this.totalPhotos(), form_type: FORM_TYPE });
      messages.push(fill(this.t.photo_total_cap, { max: MAX_PHOTOS_TOTAL, free: Math.max(0, free) }));
      candidates = candidates.slice(0, Math.max(0, free));
    }
    if (!candidates.length) { this.photoMsg(messages.join(' ')); return; }

    var prepared = [], rejected = rejectedNames.slice();
    var showOverlay = candidates.length > 1 || candidates[0].size > 2 * 1024 * 1024;
    var k = 0;
    this.preparing = (this.preparing || 0) + 1;
    this.prepProgress = { n: 1, total: candidates.length };
    this.render(); // disables Save item and Send straight away
    var next = function () {
      if (k >= candidates.length) return Promise.resolve();
      var f = candidates[k];
      self.prepProgress = { n: k + 1, total: candidates.length };
      if (showOverlay) self.showPreparing();
      k++;
      // Let the overlay paint before the CPU-heavy encode.
      return new Promise(function (r) { setTimeout(r, 0); })
        .then(function () { return self.preparePhoto(f); })
        .then(function (out) { if (out) prepared.push(out); else rejected.push(f.name); })
        .then(next);
    };
    next().catch(function () { /* an unreadable file is already in rejected */ }).then(function () {
      self.preparing = Math.max(0, self.preparing - 1);
      if (!self.preparing && self.prepBusy) { self.prepBusy = false; self.setBusy(false); }
      var current = target === self.draft; // false if the item was discarded meanwhile
      if (current) {
        if (rejected.length) messages.push(fill(self.t.photo_rejected, { names: rejected.join(', ') }));
        self.photoMsg(messages.join(' '));
      }
      if (!prepared.length || !current) { self.render(); return; }
      var wasEmpty = target.photos.length === 0;
      target.photos = target.photos.concat(prepared);
      self.draftTouched = true;
      if (wasEmpty) track('trade_in_required_photo_added', { slot: 'photos', item_index: self.itemIndex(), form_type: FORM_TYPE, file_size_mb: +(prepared[0].size / 1048576).toFixed(2) });
      track('trade_in_photo_uploaded', {
        slot: 'photos', form_type: FORM_TYPE, item_index: self.itemIndex(), photo_number: target.photos.length,
        file_size_mb: +(prepared.reduce(function (s, f) { return s + f.size; }, 0) / 1048576).toFixed(2)
      });
      self.render();
    });
  };

  P.removePhoto = function (j) {
    var f = this.draft.photos[j];
    if (!f) return;
    this.draft.photos.splice(j, 1);
    this.draftTouched = true;
    this.releaseIfUnused(f);
    this.photoMsg(null);
    track('trade_in_photo_removed', { slot: 'photos', item_index: this.itemIndex(), photo_number: j + 1, form_type: FORM_TYPE });
    if (!this.draft.photos.length) track('trade_in_required_photo_removed', { slot: 'photos', item_index: this.itemIndex(), form_type: FORM_TYPE });
    this.render();
    var thumbs = this.$$('[data-tq-remove-photo]');
    (thumbs[Math.min(j, thumbs.length - 1)] || this.$('[data-tq-action="pick"]')).focus();
  };

  P.saveItem = function () {
    if (this.preparing) { this.showPreparing(); return; }
    if (this.draftErrors().length) { this.noteBlocked(); return; }
    var item = {
      makeModel: this.draft.makeModel.trim(), condition: this.draft.condition,
      quantity: this.draft.quantity, photos: this.draft.photos.slice()
    };
    if (this.editIndex !== null) {
      var old = this.items[this.editIndex];
      this.items[this.editIndex] = item;
      var self = this;
      old.photos.forEach(function (f) { self.releaseIfUnused(f); });
    } else {
      if (this.items.length >= MAX_ITEMS) return;
      this.items.push(item);
      track('trade_in_item_added', { item_count: this.items.length, form_type: FORM_TYPE });
      if (this.items.length >= MAX_ITEMS) track('trade_in_item_cap_reached', { item_count: this.items.length, form_type: FORM_TYPE });
    }
    this.idemKey = null; // an edited submission must never replay an earlier one's result
    this.newDraft();
    this.syncDraft();
    this.setStep(3);
  };

  P.cancelItem = function () {
    var self = this;
    var leave = function () {
      var draftPhotos = self.draft.photos;
      self.newDraft();
      draftPhotos.forEach(function (f) { self.releaseIfUnused(f); });
      self.syncDraft();
      if (self.items.length) self.setStep(3); else self.render();
    };
    if (!this.draftTouched) return leave();
    this.confirm({ title: this.t.discard_title, body: this.t.discard_body, yes: this.t.discard_confirm, no: this.t.keep_editing })
      .then(function (ok) { if (ok) leave(); });
  };

  P.addAnother = function () {
    if (this.items.length >= MAX_ITEMS) return;
    this.newDraft();
    this.syncDraft();
    this.setStep(2);
  };

  P.editItem = function (i) {
    var it = this.items[i];
    if (!it) return;
    this.draft = { makeModel: it.makeModel, condition: it.condition, quantity: it.quantity, photos: it.photos.slice() };
    this.editIndex = i;
    this.draftTouched = false;
    this.draftShowErrors = false;
    this.syncDraft();
    this.setStep(2);
  };

  P.deleteItem = function (i) {
    var self = this, it = this.items[i];
    if (!it) return;
    this.confirm({ title: this.t.delete_title, body: fill(this.t.delete_body, { name: it.makeModel }), yes: this.t.delete_confirm, no: this.t.keep_editing })
      .then(function (ok) {
        if (!ok) return;
        self.items.splice(i, 1);
        it.photos.forEach(function (f) { self.releaseIfUnused(f); });
        self.idemKey = null;
        track('trade_in_item_removed', { item_count: self.items.length, form_type: FORM_TYPE });
        if (!self.items.length) return self.setStep(2);
        self.render();
        var h = self.$('#at-tq-h3'); if (h) h.focus({ preventScroll: true });
      });
  };

  // Called when a primary button is pressed while disabled (step 1 or 2).
  P.noteBlocked = function () {
    if (this.step === 1) {
      this.touched.name = this.touched.email = true;
      this.validateContact(true);
      var missing = [];
      if (!this.nameOk()) missing.push('name');
      if (!this.emailOk()) missing.push('email');
      if (!missing.length) return;
      track('trade_in_validation_blocked', { missing: missing.join('|'), step: 1, form_type: FORM_TYPE });
      // Same meaning as ATEmailCheck.gate's event: a malformed address blocked the next step.
      // Continue is disabled for a malformed address, so the gate itself never sees one.
      if (this.contact.email.trim() && !this.emailOk()) track('trade_in_email_blocked', { form_type: FORM_TYPE });
      this.$(missing[0] === 'name' ? '#at-tq-name' : '#at-tq-email').focus();
    } else if (this.step === 2) {
      if (this.preparing) { this.showPreparing(); return; }
      var errs = this.draftErrors();
      if (!errs.length) return;
      this.draftShowErrors = true;
      this.render();
      track('trade_in_validation_blocked', { item_index: this.itemIndex(), missing: errs.join('|'), missing_slots: errs.indexOf('photos') !== -1 ? 'photos' : '', step: 2, form_type: FORM_TYPE });
      var first = { mm: '#at-tq-mm', cond: 'input[name="at-tq-condition"]', photos: '[data-tq-action="pick"]' }[errs[0]];
      var el = this.$(first);
      if (el) { el.focus({ preventScroll: true }); el.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' }); }
    }
  };

  // ----- step 3: send -----

  P.submit = function () {
    var self = this, t = this.t;
    var endpoint = (this.cfg.endpoints || {}).tradeIns;
    if (this.submitting || !endpoint || !this.items.length || !this.contactOk()) return;
    if (this.preparing) { this.showPreparing(); return; }
    if (RETRY && !this.idemKey) this.idemKey = uuid();
    var idemKey = RETRY ? this.idemKey : null;
    this.submitting = true;
    this.render();

    var items = this.items;
    var totalPhotos = items.reduce(function (n, it) { return n + it.photos.length; }, 0);
    var bar = this.$('[data-tq-busy-bar]'), barFill = bar.querySelector('span');
    this.setBusy(true, t.sending_title, '');
    this.$('[data-tq-busy-hint]').hidden = false;
    barFill.style.width = '0%';

    // Flatten every item's photos. item{N}_ is a human-readable breadcrumb for whoever opens
    // the deal. The API has no photo-to-item association, so build no behaviour on it.
    var all = [];
    items.forEach(function (it, i) { it.photos.forEach(function (f, j) { all.push({ file: f, name: 'item' + i + '_photo' + j + '_' + f.name }); }); });

    var prepared = [];
    var k = 0;
    var compressNext = function () {
      if (k >= all.length) return Promise.resolve();
      var p = all[k++];
      if (isHeic(p.file)) { prepared.push({ file: p.file, name: p.name }); return compressNext(); }
      self.$('[data-tq-busy-text]').textContent = fill(t.sending_prepare, { n: k, total: all.length });
      return compressImage(p.file).then(function (c) {
        c = c || p.file;
        // A re-encoded JPEG must be named .jpg: the API magic-byte checks content against extension.
        prepared.push({ file: c, name: c === p.file ? p.name : p.name.replace(/\.[^.]+$/, '') + '.jpg' });
      }).then(compressNext);
    };

    compressNext().then(function () {
      bar.hidden = false;
      self.readGaCookie();
      self.readConsent();
      var fd = new FormData();
      fd.append('source', 'shopify-trade-in');
      fd.append('contact_email', self.contact.email.trim());
      fd.append('contact_name', self.contact.name.trim().slice(0, 120));
      fd.append('capture_id', self.captureId);
      fd.append('tracking', JSON.stringify(self.tracking));
      // Make/model split on the first space is the old form's status quo: known fragile, but
      // the API needs only one of make or model per item.
      fd.append('items', JSON.stringify(items.map(function (it) {
        var parts = it.makeModel.trim().split(/\s+/);
        return { make: parts[0] || it.makeModel.trim(), model: parts.slice(1).join(' ') || '', condition: it.condition, quantity: it.quantity };
      })));
      prepared.forEach(function (p) { fd.append('photos', p.file, p.name); });

      var sendOnce = function () {
        return new Promise(function (resolve, reject) {
          var xhr = new XMLHttpRequest();
          xhr.upload.addEventListener('progress', function (e) {
            if (!e.lengthComputable) return;
            var pct = Math.round((e.loaded / e.total) * 100);
            barFill.style.width = pct + '%';
            self.$('[data-tq-busy-text]').textContent = fill(t.sending_upload, {
              loaded: (e.loaded / 1048576).toFixed(1), total: (e.total / 1048576).toFixed(1), percent: pct
            });
          });
          xhr.addEventListener('load', function () {
            if (xhr.status >= 200 && xhr.status < 300) {
              try { return resolve(JSON.parse(xhr.responseText)); } catch (e) { var bad = new Error('Invalid response from server'); bad.statusCode = xhr.status; return reject(bad); }
            }
            var msg = '', itemIndex = null;
            try {
              var body = JSON.parse(xhr.responseText);
              msg = [body.detail, body.error].filter(function (v) { return typeof v === 'string'; })[0] || '';
              if (!msg && Array.isArray(body.detail)) {
                msg = body.detail.map(function (d) { return ((d.loc || []).slice(1).join('.') || 'body') + ': ' + (d.msg || 'invalid'); }).join('; ');
              }
              if (typeof body.field === 'string') { var m = body.field.match(/^items\[(\d+)\]$/); if (m) itemIndex = parseInt(m[1], 10); }
            } catch (e) { /* not JSON */ }
            var err = new Error(msg || ('Server error (' + xhr.status + ')'));
            err.statusCode = xhr.status;
            if (itemIndex !== null) err.itemIndex = itemIndex;
            reject(err);
          });
          xhr.addEventListener('error', function () { var e = new Error('Network error'); e.errorType = 'network'; reject(e); });
          xhr.addEventListener('timeout', function () { var e = new Error('Upload timed out'); e.errorType = 'timeout'; reject(e); });
          xhr.open('POST', endpoint);
          if (idemKey) xhr.setRequestHeader('Idempotency-Key', idemKey);
          xhr.timeout = 120000;
          xhr.send(fd);
        });
      };

      var attempt = 0;
      var loop = function () {
        return sendOnce().catch(function (err) {
          attempt++;
          // Retry only transient failures. 409 is the guard's "request in progress".
          var retryable = RETRY && attempt < 3 && (err.errorType === 'network' || err.errorType === 'timeout' || err.statusCode === 503 || err.statusCode === 409);
          if (!retryable) throw err;
          track('trade_in_retry', { attempt: attempt, form_type: FORM_TYPE, error_type: err.errorType || (err.statusCode ? 'http_' + err.statusCode : 'unknown') });
          self.$('[data-tq-busy-text]').textContent = fill(t.sending_retry, { attempt: attempt });
          return new Promise(function (r) { setTimeout(r, (attempt === 1 ? 1000 : 2000) + Math.floor(Math.random() * 300)); }).then(loop);
        });
      };

      return loop().then(function (result) {
        self.setBusy(false);
        track('trade_in_submit', {
          item_count: items.length,
          total_quantity: items.reduce(function (n, it) { return n + it.quantity; }, 0),
          total_photos: totalPhotos,
          additional_photos: 0,
          required_filled: items.length,
          page_path: location.pathname,
          form_type: FORM_TYPE,
          retry_attempts: attempt
        });
        self.showDone(result || {});
      }, function (error) {
        self.setBusy(false);
        self.submitting = false;
        var files = items.reduce(function (a, it) { return a.concat(it.photos); }, []);
        track('trade_in_error', {
          error_message: (error.message || '').substring(0, 100),
          error_type: error.errorType || (error.statusCode ? 'http_' + error.statusCode : 'unknown'),
          retry_attempts: Math.max(0, attempt - 1),
          item_count: items.length,
          total_photos: files.length,
          upload_size_mb: parseFloat((files.reduce(function (s, f) { return s + (f.size || 0); }, 0) / 1048576).toFixed(2))
        });
        var text;
        var invalid = error.statusCode === 400 || error.statusCode === 422;
        if (error.statusCode === 429) text = t.error_rate;
        else if (error.errorType === 'network') text = t.error_network;
        else if (error.errorType === 'timeout') text = t.error_timeout;
        // The API rejected what was sent: the customer can fix it, so this is not "our side".
        // The copy supplies its own full stop, so drop any the API's reason ends with.
        else if (invalid) text = fill(t.error_invalid, { detail: String(error.message || '').replace(/[.\s]+$/, '') });
        else text = fill(t.error_server, { detail: error.message });
        self.render();
        var badItem = invalid && typeof error.itemIndex === 'number' && items[error.itemIndex] ? error.itemIndex : null;
        self.confirm({ title: t.error_title, body: text, yes: t.error_dismiss, tone: 'primary' }).then(function () {
          // Like the old form's highlighted card: take them straight to the item the API rejected.
          if (badItem !== null) self.editItem(badItem);
        });
      });
    });
  };

  P.showDone = function (result) {
    this.done = true;
    this.submitting = false;
    if (this.resumed) this.resumed.captureId = null; // that capture is converted; the next form gets a new id
    this.result = { reference: result.reference_code || '', token: result.notes_token || '' };
    var ref = this.$('[data-tq-ref]');
    if (this.result.reference) {
      ref.innerHTML = esc(fill(this.t.success_ref, { reference: '\u0000' })).replace('\u0000', '<strong>' + esc(this.result.reference) + '</strong>');
      ref.hidden = false;
    } else ref.hidden = true;
    var canNote = !!(this.result.reference && this.result.token && (this.cfg.endpoints || {}).notes);
    this.$('[data-tq-note]').hidden = !canNote;
    this.noteAgain(true);
    this.render();
    var scroll = this.$('[data-tq-scroll]'); if (scroll) scroll.scrollTop = 0;
    this.focusStep(false);
    this.trackStep();
  };

  P.submitAnother = function () {
    // A new form session: the last capture converted, so this one gets its own id.
    this.revokeAll();
    this.reset(true);
    this.syncInputs();
    this.capture();
    this.setStep(2);
  };

  // ----- notes -----

  P.sendNote = function () {
    var self = this, ta = this.$('#at-tq-note'), note = ta.value.trim();
    var btn = this.$('[data-tq-action="note"]');
    if (!note || this.noteBusy || !this.result) return;
    if (note.length > MAX_NOTE) { this.fieldError('note', fill(this.t.note_too_long, { max: MAX_NOTE })); return; }
    var url = fill(this.cfg.endpoints.notes, { reference: encodeURIComponent(this.result.reference) });
    this.noteBusy = true; btn.disabled = true;
    window.fetch(url, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'omit', mode: 'cors',
      body: JSON.stringify({ token: this.result.token, note: note })
    }).then(function (res) {
      if (!res.ok) throw new Error('http_' + res.status);
      self.noteBusy = false;
      self.$('[data-tq-note-form]').hidden = true;
      self.$('[data-tq-note-sent]').hidden = false;
      self.$('.at-tq__note-sent').focus({ preventScroll: true });
    }).catch(function () {
      self.noteBusy = false; btn.disabled = false;
      self.fieldError('note', self.t.note_error);
    });
  };

  P.noteAgain = function (silent) {
    var ta = this.$('#at-tq-note');
    ta.value = '';
    this.fieldError('note', null);
    this.$('[data-tq-note-form]').hidden = false;
    this.$('[data-tq-note-sent]').hidden = true;
    this.$('[data-tq-action="note"]').disabled = true;
    if (silent !== true) ta.focus();
  };

  // ----- tracking (carried over) -----

  P.captureTracking = function () {
    var self = this, params = new URLSearchParams(location.search);
    TRACKING_FIELDS.forEach(function (p) {
      var v = params.get(p) || session(true, 'at_' + p);
      if (v) { self.tracking[p] = v; session(false, 'at_' + p, v); }
    });
    if (!this.tracking.ga4_client_id && typeof window.gtag !== 'undefined' && window.GA_MEASUREMENT_ID) {
      try {
        window.gtag('get', window.GA_MEASUREMENT_ID, 'client_id', function (id) {
          if (id) { self.tracking.ga4_client_id = id; session(false, 'at_ga4_client_id', id); }
        });
      } catch (e) { /* ignore */ }
    }
    this.readGaCookie();
  };

  P.readGaCookie = function () {
    if (this.tracking.ga4_client_id) return;
    try {
      var ga = document.cookie.split(';').filter(function (c) { return c.trim().indexOf('_ga=') === 0; })[0];
      if (!ga) return;
      var parts = ga.split('=')[1].split('.');
      if (parts.length >= 4) { this.tracking.ga4_client_id = parts.slice(-2).join('.'); session(false, 'at_ga4_client_id', this.tracking.ga4_client_id); }
    } catch (e) { /* ignore */ }
  };

  // Marketing consent read at send time: the visitor may answer the banner after page load.
  // Meta click ids only with marketing consent, the same gate the Meta Pixel respects.
  P.readConsent = function () {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.currentVisitorConsent !== 'function') return;
      var consent = cp.currentVisitorConsent();
      if (consent && (consent.marketing === 'yes' || consent.marketing === 'no')) this.tracking.ad_user_data_consent = consent.marketing;
      if (consent && consent.marketing === 'yes') {
        var read = function (name) {
          var hit = document.cookie.split(';').filter(function (c) { return c.trim().indexOf(name + '=') === 0; })[0];
          return hit ? hit.split('=').slice(1).join('=') : '';
        };
        var fbp = read('_fbp'), fbc = read('_fbc');
        if (fbp) this.tracking.fbp = fbp;
        if (fbc) this.tracking.fbc = fbc;
      }
    } catch (e) { /* never blocks sending */ }
  };

  // ----- object URLs -----

  P.urlFor = function (f) {
    if (!this.urls.has(f)) this.urls.set(f, URL.createObjectURL(f));
    return this.urls.get(f);
  };
  P.releaseIfUnused = function (f) {
    var used = this.draft.photos.indexOf(f) !== -1 || this.items.some(function (it) { return it.photos.indexOf(f) !== -1; });
    if (used || !this.urls.has(f)) return;
    URL.revokeObjectURL(this.urls.get(f));
    this.urls.delete(f);
  };
  P.revokeAll = function () {
    this.urls.forEach(function (u) { URL.revokeObjectURL(u); });
    this.urls.clear();
  };

  P.thumbHtml = function (f, cls) {
    if (!f) return '<span class="' + cls + '">' + ICON.image + '</span>';
    if (this.noThumb.has(f)) return '<span class="' + cls + '">' + esc(extOf(f).toUpperCase()) + '</span>';
    return '<img class="' + cls + '" src="' + esc(this.urlFor(f)) + '" alt="">';
  };

  // ----- rendering -----

  // Inputs are written only on a state reset, never while someone types.
  P.syncInputs = function () {
    this.$('#at-tq-name').value = this.contact.name;
    this.$('#at-tq-email').value = this.contact.email;
    this.fieldError('name', null);
    this.fieldError('email', null);
    this.syncDraft();
  };

  P.syncDraft = function () {
    var d = this.draft;
    this.$('#at-tq-mm').value = d.makeModel;
    this.$('#at-tq-qty').value = d.quantity;
    this.$$('input[name="at-tq-condition"]').forEach(function (r) { r.checked = r.value === d.condition; });
    this.photoMsg(null);
  };

  P.itemMeta = function (it) {
    return fill(this.t.item_meta, { condition: it.condition, quantity: it.quantity, photos: this.plural(it.photos.length, 'photos_one', 'photos_other') });
  };

  P.render = function () {
    var self = this, t = this.t, step = this.step, done = this.done;
    var n = this.items.length;

    // Header
    this.$('#at-tq-title').textContent = done ? t.title_done : t.title;
    this.$('#at-tq-status').textContent = done ? t.status_done
      : fill(t.step_counter, { step: step, total: 3 }) + ' · ' + this.plural(n, 'saved_one', 'saved_other');

    // Panels
    this.$('[data-tq-form]').hidden = done;
    this.$('[data-tq-panel="done"]').hidden = !done;
    this.$('[data-tq-footer]').hidden = done;
    [1, 2, 3].forEach(function (s) { self.$('[data-tq-panel="' + s + '"]').hidden = s !== step; });

    // Stepper
    this.$$('.at-tq__step').forEach(function (b) {
      var s = parseInt(b.getAttribute('data-tq-goto'), 10);
      var isDone = s < step && self.canGoTo(s + 1);
      b.classList.toggle('is-current', s === step);
      b.classList.toggle('is-done', isDone);
      if (s === step) b.setAttribute('aria-current', 'step'); else b.removeAttribute('aria-current');
      b.disabled = s !== step && !self.canGoTo(s);
      b.querySelector('[data-tq-step-sr]').textContent = isDone ? ', ' + t.step_done : '';
    });

    // Step 1
    var p1 = this.$('[data-tq-primary="1"]'), p2 = this.$('[data-tq-primary="2"]'), p3 = this.$('[data-tq-primary="3"]');
    p1.hidden = step !== 1; p2.hidden = step !== 2; p3.hidden = step !== 3;
    p1.disabled = !this.contactOk();

    // Step 2
    var d = this.draft, errs = this.draftErrors();
    this.$('[data-tq-qty="-1"]').disabled = d.quantity <= 1;
    this.$('[data-tq-qty="1"]').disabled = d.quantity >= MAX_QTY;
    var hint = this.$('[data-tq-photos-hint]');
    hint.textContent = fill(t.photos_hint, { max: MAX_PHOTOS_PER_ITEM, count: d.photos.length });
    this.$('[data-tq-action="pick"]').disabled = d.photos.length >= MAX_PHOTOS_PER_ITEM || this.totalPhotos() >= MAX_PHOTOS_TOTAL;
    var thumbs = this.$('[data-tq-thumbs]');
    var html = d.photos.map(function (f, j) {
      var label = fill(t.photo_remove, { n: j + 1 });
      var body = self.noThumb.has(f)
        ? '<span class="at-tq__thumb-ph" role="img" aria-label="' + esc(fill(t.photo_alt, { n: j + 1 })) + '">' + ICON.check + esc(extOf(f).toUpperCase()) + '</span>'
        : '<img src="' + esc(self.urlFor(f)) + '" alt="' + esc(fill(t.photo_alt, { n: j + 1 })) + '">';
      return '<li class="at-tq__thumb">' + body + '<button type="button" class="at-tq__remove" data-tq-remove-photo="' + j + '" aria-label="' + esc(label) + '">' + ICON.x + '</button></li>';
    }).join('');
    if (thumbs._html !== html) { thumbs.innerHTML = html; thumbs._html = html; }
    var show = this.draftShowErrors;
    this.fieldError('mm', show && errs.indexOf('mm') !== -1 ? t.mm_error : null);
    this.fieldError('cond', show && errs.indexOf('cond') !== -1 ? t.cond_error : null);
    this.fieldError('photos', show && errs.indexOf('photos') !== -1 ? t.photos_error : null);
    if (show && !errs.length) this.draftShowErrors = false;
    p2.disabled = errs.length > 0 || this.preparing > 0;
    this.$('[data-tq-action="cancel"]').hidden = step !== 2 || (n === 0 && this.editIndex === null);

    // Sidebar (step 2, 1024px and up)
    var aside = this.$('[data-tq-aside]');
    aside.classList.toggle('is-shown', step === 2 && !done);
    this.$('[data-tq-aside-heading]').textContent = fill(t.sidebar_heading, { count: n });
    this.$('[data-tq-aside-empty]').hidden = n > 0;
    var asideHtml = this.items.map(function (it) {
      return '<li class="at-tq__item">' + self.thumbHtml(it.photos[0], 'at-tq__item-img') +
        '<div class="at-tq__item-text"><p class="at-tq__item-name">' + esc(it.makeModel) + '</p><p class="at-tq__item-meta">' + esc(self.itemMeta(it)) + '</p></div></li>';
    }).join('');
    var asideList = this.$('[data-tq-aside-list]');
    if (asideList._html !== asideHtml) { asideList.innerHTML = asideHtml; asideList._html = asideHtml; }
    asideList.hidden = n === 0;

    // Step 3
    this.$('[data-tq-items-heading]').textContent = fill(t.items_heading, { count: n });
    var reviewHtml = this.items.map(function (it, i) {
      return '<li class="at-tq__item">' + self.thumbHtml(it.photos[0], 'at-tq__item-img') +
        '<div class="at-tq__item-text"><p class="at-tq__item-name">' + esc(it.makeModel) + '</p><p class="at-tq__item-meta">' + esc(self.itemMeta(it)) + '</p></div>' +
        '<div class="at-tq__item-actions">' +
        '<button type="button" class="at-tq__icon-btn" data-tq-edit="' + i + '" aria-label="' + esc(fill(t.edit_item, { name: it.makeModel })) + '">' + ICON.pencil + '</button>' +
        '<button type="button" class="at-tq__icon-btn at-tq__icon-btn--danger" data-tq-delete="' + i + '" aria-label="' + esc(fill(t.remove_item, { name: it.makeModel })) + '">' + ICON.trash + '</button>' +
        '</div></li>';
    }).join('');
    var list = this.$('[data-tq-review-list]');
    if (list._html !== reviewHtml) { list.innerHTML = reviewHtml; list._html = reviewHtml; }
    var atCap = n >= MAX_ITEMS;
    this.$$('[data-tq-action="add"]').forEach(function (b) { b.disabled = atCap; });
    var cap = this.$('[data-tq-cap-note]');
    cap.hidden = !atCap;
    if (atCap && !cap._filled) {
      cap.innerHTML = esc(fill(t.cap_note, { max: MAX_ITEMS })) + ' <a href="' + esc((this.cfg.urls || {}).bulk || '/pages/sell-music-gear-bulk') + '" data-tq-bulk>' + esc(t.cap_link) + '</a>';
      cap._filled = true;
    }
    this.$('[data-tq-contact-name]').textContent = this.contact.name.trim();
    this.$('[data-tq-contact-email]').textContent = this.contact.email.trim();
    var canSend = !!(this.cfg.endpoints || {}).tradeIns;
    this.$('[data-tq-send-off]').hidden = canSend;
    p3.disabled = !canSend || this.submitting || !n || this.preparing > 0;

    // Footer
    var back = this.$('[data-tq-action="back"]');
    back.hidden = step === 3;
    back.disabled = step === 1;
    this.$('[data-tq-foot="add"]').hidden = step !== 3;

    // Screen-reader reason for a disabled primary button
    var why = '';
    if (step === 1 && !this.contactOk()) why = [!this.nameOk() ? t.name_error : '', !this.emailOk() ? t.email_error : ''].filter(Boolean).join(' ');
    if (step === 2 && errs.length) {
      why = fill(t.save_blocked, { missing: errs.map(function (e) { return { mm: t.mm_error, cond: t.cond_error, photos: t.photos_error }[e]; }).join(' ') });
    }
    this.$('#at-tq-blocked').textContent = why;
  };

  // ---------- resume link ----------

  // POSTs the key once to <capture endpoint>/resume. Resolves with { name, email, captureId }
  // on a 200 with a usable body, or null on anything else (non-200, network, timeout, bad
  // JSON). A failure shows nothing: the form opens as it would have without the link.
  function requestResume(cfg, key) {
    var base = String((cfg.endpoints || {}).captures || '').replace(/\/+$/, '');
    if (!base || !window.fetch) return Promise.resolve(null);
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timedOut = false;
    var timer = setTimeout(function () { timedOut = true; if (ctrl) ctrl.abort(); }, RESUME_TIMEOUT);
    var opts = {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'omit', mode: 'cors',
      cache: 'no-store', body: JSON.stringify({ token: key })
    };
    if (ctrl) opts.signal = ctrl.signal;
    var p;
    try { p = window.fetch(base + '/resume', opts); } catch (e) { clearTimeout(timer); return Promise.resolve(null); }
    return p.then(function (res) {
      if (res.status !== 200) return null;
      return res.json();
    }).then(function (body) {
      clearTimeout(timer);
      if (timedOut || !body || typeof body !== 'object') return null;
      var name = typeof body.contact_name === 'string' ? body.contact_name.trim() : '';
      var email = typeof body.contact_email === 'string' ? body.contact_email.trim() : '';
      var id = typeof body.capture_id === 'string' ? body.capture_id.trim() : '';
      if (!name || !email || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)) return null;
      return { name: name, email: email, captureId: id.toLowerCase() };
    }).catch(function () { clearTimeout(timer); return null; });
  }

  // ---------- boot ----------

  function boot() {
    var tpl = document.querySelector('template[data-at-tq-template]');
    var cfgEl = document.querySelector('script[data-at-tq-config]');
    if (!tpl || !cfgEl) return;
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    var root = tpl.content.firstElementChild.cloneNode(true);
    var form = new TradeQuote(cfg, root);
    form.mount();
    window.ATTradeQuote = form;
    form.watchLaunchers();

    // Sent once, before the auto-open below, so the reply often lands before the dialog paints.
    // A slower reply fills the open dialog in place (applyResume).
    var resume = function (key) {
      requestResume(cfg, key).then(function (r) { if (r) form.applyResume(r); });
    };
    if (resumeKey) { resume(resumeKey); resumeKey = null; }

    document.addEventListener('click', function (e) {
      var opener = e.target.closest && e.target.closest('[data-at-tq-open]');
      if (!opener) return;
      e.preventDefault();
      form.open('click');
    });

    // SEO landing page view (retry until gtag exists: GTM loads async).
    if (/^\/pages\/sell-/.test(location.pathname)) {
      var fire = function (n) {
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'sell_landing_page_view', { page_handle: location.pathname.replace('/pages/', ''), page_title: document.title, referrer: document.referrer || '(direct)' });
        } else if (n < 15) setTimeout(function () { fire(n + 1); }, 200);
      };
      fire(0);
    }

    // Opens by itself on /pages/trade-in (autoOpen), for ?quote=start (the chase emails), and
    // for #trade-form, the old embedded form's id, which old links and bookmarks still carry.
    var params = new URLSearchParams(location.search);
    var byLink = params.get('quote') === 'start' || location.hash === '#trade-form';
    if (cfg.autoOpen || byLink) form.open(byLink ? 'link' : 'auto');
    // A resume link opened on a page that is already showing arrives as a hash change.
    window.addEventListener('hashchange', function () {
      if (location.hash === '#trade-form') return form.open('link');
      var key = takeResumeKey();
      if (key) { resume(key); form.open('link'); }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
